import React, { useState, useEffect } from 'react';
import {
  Loader2, X, Phone, Save, Plus, Shield, Calendar, AlertTriangle, Trash2, EyeOff,
  Crown, Tag, StickyNote, Coins,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Composants partagés extraits de l'ancien "ClientSearchPanel" — réutilisés par la
// fiche client unifiée (onglet "Clients") ET par l'onglet "Accès Client" (zone danger).

export const PLAN_OPTIONS = [
  { value: '', label: '— Désactiver / Aucun plan' },
  { value: 'communaute', label: 'Communauté Body-Up — 49 CHF/mois (coaching inclus)' },
  { value: 'transformation', label: 'Pack Transformation — 299 CHF/mois (coaching inclus)' },
  { value: 'vip', label: 'Coaching Hybride / VIP — 399 CHF/mois (coaching inclus)' },
  { value: 'monthly', label: 'Suivi Pro seul — 9.90 CHF/mois (SANS coaching)' },
  { value: 'yearly', label: 'Suivi Pro seul — 79 CHF/an (SANS coaching)' },
];

export function PlanManager({ email, csrfToken, subscription, clientName, onSaved }) {
  const [plan, setPlan] = useState(subscription?.plan || '');
  const [endDate, setEndDate] = useState(subscription?.current_period_end?.slice(0, 10) || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/clients/${encodeURIComponent(email)}/set-plan`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
        body: JSON.stringify({
          plan: plan || null,
          current_period_end: endDate || null,
          name: clientName || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erreur');
      toast.success(
        plan
          ? `Plan "${plan}" activé pour ${email}${data.account_created ? ' — compte créé + email de bienvenue envoyé' : ' — email envoyé au client'}`
          : `Accès désactivé pour ${email}`
      );
      await onSaved?.();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const currentPlanLabel = PLAN_OPTIONS.find(o => o.value === (subscription?.plan || ''))?.label || 'Aucun plan actif';
  const isActive = subscription?.status === 'active';

  return (
    <div className="rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/5 to-zinc-900/60 p-4 space-y-3" data-testid="plan-manager">
      <div className="flex items-center justify-between">
        <h4 className="flex items-center gap-2 font-bold text-white text-sm">
          <Shield className="w-4 h-4 text-primary" /> Gestion du plan d'accès
        </h4>
        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${isActive ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/25' : 'bg-zinc-700/40 text-zinc-500 border-zinc-600/30'}`}>
          {isActive ? '● Actif' : '○ Inactif'}
        </span>
      </div>
      {isActive && (
        <p className="text-xs text-zinc-400">
          Plan actuel : <span className="text-white font-semibold">{currentPlanLabel}</span>
          {subscription?.current_period_end && (
            <span className="text-zinc-500"> · expire le {new Date(subscription.current_period_end).toLocaleDateString('fr-FR')}</span>
          )}
          {!subscription?.current_period_end && <span className="text-zinc-500"> · accès permanent</span>}
        </p>
      )}
      <div className="flex flex-col sm:flex-row gap-2">
        <select value={plan} onChange={e => setPlan(e.target.value)}
          className="flex-1 bg-zinc-800 border border-white/10 rounded-xl px-3 py-2 text-white text-sm focus:border-primary focus:outline-none"
          data-testid="plan-select">
          {PLAN_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <div className="flex items-center gap-1.5">
          <Calendar className="w-4 h-4 text-zinc-500 shrink-0" />
          <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
            className="bg-zinc-800 border border-white/10 rounded-xl px-3 py-2 text-zinc-300 text-sm focus:border-primary focus:outline-none"
            placeholder="Date de fin (optionnel)" title="Laisser vide = accès permanent"
            data-testid="plan-end-date" />
        </div>
        <button onClick={handleSave} disabled={saving}
          className="px-4 py-2 rounded-xl font-bold text-white text-sm bg-gradient-to-r from-primary to-pink-500 hover:scale-[1.02] transition-transform disabled:opacity-50 shrink-0"
          data-testid="plan-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-3.5 h-3.5 inline mr-1" />Appliquer</>}
        </button>
      </div>
      <p className="text-[10px] text-zinc-600">Sans date de fin = accès permanent jusqu'à désactivation manuelle.</p>
    </div>
  );
}

/**
 * Zone danger : suppression définitive ou anonymisation RGPD d'un compte client.
 * Requiert un motif (audit) + une double confirmation (case à cocher + saisie de l'email).
 */
export function DangerZone({ userId, email, csrfToken, onDeleted }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('anonymize');
  const [reason, setReason] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const canSubmit = reason.trim().length >= 3 && confirmEmail.trim().toLowerCase() === email.toLowerCase();

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/clients/by-id/${encodeURIComponent(userId)}/delete-or-anonymize`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
        body: JSON.stringify({ confirm: true, reason: reason.trim(), mode }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erreur');
      toast.success(mode === 'delete' ? `Compte ${email} supprimé définitivement` : `Compte ${email} anonymisé`);
      onDeleted?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-red-500/25 bg-red-500/5 rounded-2xl p-4" data-testid="danger-zone">
      <h4 className="flex items-center gap-2 font-bold text-red-300 text-sm mb-1">
        <AlertTriangle className="w-4 h-4" /> Zone danger
      </h4>
      <p className="text-zinc-500 text-xs mb-3">Suppression définitive ou anonymisation RGPD du compte client (irréversible).</p>

      {!open ? (
        <button
          onClick={() => setOpen(true)}
          className="px-3 py-1.5 rounded-lg text-xs font-semibold text-red-300 bg-red-500/10 hover:bg-red-500/20 border border-red-500/25 inline-flex items-center gap-1.5"
          data-testid="danger-zone-open-btn"
        >
          <Trash2 className="w-3.5 h-3.5" /> Supprimer / anonymiser ce compte
        </button>
      ) : (
        <div className="space-y-3">
          <div className="flex gap-2">
            <button
              onClick={() => setMode('anonymize')}
              className={`flex-1 px-3 py-2 rounded-lg text-xs font-semibold inline-flex items-center justify-center gap-1.5 border transition-colors ${mode === 'anonymize' ? 'bg-amber-500/20 border-amber-500/40 text-amber-300' : 'bg-white/5 border-white/10 text-zinc-400'}`}
              data-testid="danger-zone-mode-anonymize"
            >
              <EyeOff className="w-3.5 h-3.5" /> Anonymiser (garde les stats)
            </button>
            <button
              onClick={() => setMode('delete')}
              className={`flex-1 px-3 py-2 rounded-lg text-xs font-semibold inline-flex items-center justify-center gap-1.5 border transition-colors ${mode === 'delete' ? 'bg-red-500/20 border-red-500/40 text-red-300' : 'bg-white/5 border-white/10 text-zinc-400'}`}
              data-testid="danger-zone-mode-delete"
            >
              <Trash2 className="w-3.5 h-3.5" /> Supprimer définitivement
            </button>
          </div>
          <div>
            <label className="block text-zinc-500 text-[10px] mb-1">Motif (obligatoire, pour l'audit)</label>
            <input
              type="text" value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="Ex: demande RGPD du client, compte test à nettoyer…"
              className="w-full px-3 py-2 bg-zinc-800 border border-white/10 rounded-lg text-white text-sm focus:border-red-400 focus:outline-none"
              data-testid="danger-zone-reason"
            />
          </div>
          <div>
            <label className="block text-zinc-500 text-[10px] mb-1">
              Tapez <span className="text-white font-mono">{email}</span> pour confirmer
            </label>
            <input
              type="text" value={confirmEmail} onChange={(e) => setConfirmEmail(e.target.value)}
              placeholder={email}
              className="w-full px-3 py-2 bg-zinc-800 border border-white/10 rounded-lg text-white text-sm focus:border-red-400 focus:outline-none"
              data-testid="danger-zone-confirm-email"
            />
          </div>
          {error && <p className="text-red-400 text-xs" data-testid="danger-zone-error">{error}</p>}
          <div className="flex gap-2">
            <button
              onClick={() => setOpen(false)}
              className="px-3 py-2 rounded-lg text-xs font-semibold text-zinc-400 bg-white/5 hover:bg-white/10"
              data-testid="danger-zone-cancel-btn"
            >
              Annuler
            </button>
            <button
              onClick={submit}
              disabled={!canSubmit || busy}
              className="flex-1 px-3 py-2 rounded-lg text-xs font-bold text-white bg-red-600 hover:bg-red-500 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-1.5"
              data-testid="danger-zone-confirm-btn"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
              Confirmer {mode === 'delete' ? 'la suppression' : "l'anonymisation"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export const ClientStat = ({ label, value, color }) => (
  <div className="bg-white/5 border border-white/10 rounded-xl p-3">
    <div className="text-2xl font-extrabold" style={{ color }}>{value}</div>
    <div className="text-xs text-zinc-400 mt-0.5">{label}</div>
  </div>
);

export const SectionCard = ({ title, icon: Icon, children }) => (
  <div className="bg-zinc-900/40 border border-white/10 rounded-2xl p-4">
    <h4 className="text-white font-semibold text-sm mb-3 inline-flex items-center gap-2"><Icon className="w-4 h-4 text-violet-400" /> {title}</h4>
    {children}
  </div>
);

export const Row = ({ label, icon: Icon, value }) => (
  <div className="flex justify-between gap-2">
    <span className="text-zinc-500 inline-flex items-center gap-1">{Icon && <Icon className="w-3 h-3" />} {label}</span>
    <span className="text-white text-right">{value || '—'}</span>
  </div>
);

// ============================================================
// Éditeur VIP / Tags / Notes
// ============================================================
const SUGGESTED_TAGS = ['VIP', 'Débutant', 'Confirmé', 'Sénior', 'Préfère matin', 'Préfère soir', 'Allergique', 'Blessure'];

/** Attribution manuelle des crédits (IA muscu/cardio, coach certifié, plan alimentaire) —
 * composant autonome (fetch/save internes), pour permettre à l'admin de créditer un client
 * en quelques clics depuis sa fiche. */
const toCustomExDraft = (d) => ({
  custom_ex_included_remaining: d?.included_remaining ?? 0,
  custom_ex_purchased_remaining: d?.purchased_remaining ?? 0,
});

export const CreditsEditor = ({ email, csrfToken }) => {
  const [state, setState] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchWithCsrf = (url, options = {}) => fetch(url, {
    ...options,
    credentials: 'include',
    headers: { ...options.headers, 'X-CSRF-Token': csrfToken, 'Content-Type': 'application/json' },
  });

  const load = () => {
    setLoading(true);
    fetch(`${BACKEND_URL}/api/admin/billing/clients/credits?email=${encodeURIComponent(email)}`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => { setState(d); setDraft({ ...d.workout, ...d.meal_plan, ...toCustomExDraft(d.custom_exercises) }); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [email]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading || !draft) {
    return (
      <div className="bg-zinc-900/40 border border-white/10 rounded-2xl p-4 sm:p-5 flex items-center justify-center" data-testid="credits-editor-loading">
        <Loader2 className="w-5 h-5 text-primary animate-spin" />
      </div>
    );
  }

  const dirty = state && (
    Number(state.workout.ai_included_remaining) !== Number(draft.ai_included_remaining) ||
    Number(state.workout.ai_purchased) !== Number(draft.ai_purchased) ||
    Number(state.workout.coach_request_remaining) !== Number(draft.coach_request_remaining) ||
    Number(state.meal_plan.included_remaining) !== Number(draft.included_remaining) ||
    Number(state.meal_plan.purchased) !== Number(draft.purchased) ||
    Number(state.custom_exercises.included_remaining) !== Number(draft.custom_ex_included_remaining) ||
    Number(state.custom_exercises.purchased_remaining) !== Number(draft.custom_ex_purchased_remaining)
  );

  const save = () => {
    setSaving(true);
    fetchWithCsrf(`${BACKEND_URL}/api/admin/billing/clients/credits?email=${encodeURIComponent(email)}`, {
      method: 'PUT',
      body: JSON.stringify({
        ai_included_remaining: parseInt(draft.ai_included_remaining, 10) || 0,
        ai_purchased: parseInt(draft.ai_purchased, 10) || 0,
        coach_request_remaining: parseInt(draft.coach_request_remaining, 10) || 0,
        meal_included_remaining: parseInt(draft.included_remaining, 10) || 0,
        meal_purchased: parseInt(draft.purchased, 10) || 0,
        custom_ex_included_remaining: parseInt(draft.custom_ex_included_remaining, 10) || 0,
        custom_ex_purchased_remaining: parseInt(draft.custom_ex_purchased_remaining, 10) || 0,
      }),
    }).then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => { setState(d); setDraft({ ...d.workout, ...d.meal_plan, ...toCustomExDraft(d.custom_exercises) }); toast.success('Crédits mis à jour.'); })
      .catch(() => toast.error('Erreur lors de la mise à jour des crédits.'))
      .finally(() => setSaving(false));
  };

  const field = (label, key, hint) => (
    <div>
      <label className="block text-zinc-500 text-[10px] mb-1">{label}</label>
      <input type="number" min="0" step="1" value={draft[key] ?? 0}
        onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
        className="w-full px-3 py-2 bg-zinc-800 border border-white/10 rounded-lg text-white text-sm focus:border-primary focus:outline-none"
        data-testid={`credits-input-${key}`} />
      {hint && <p className="text-zinc-600 text-[10px] mt-0.5">{hint}</p>}
    </div>
  );

  return (
    <div className="bg-zinc-900/40 border border-white/10 rounded-2xl p-4 sm:p-5 space-y-4" data-testid="credits-editor">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h4 className="text-white font-semibold inline-flex items-center gap-2">
          <Coins className="w-4 h-4 text-yellow-400" /> Crédits du client
        </h4>
        {dirty && (
          <button onClick={save} disabled={saving}
            className="px-3 py-1.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white text-xs font-semibold rounded-lg inline-flex items-center gap-1.5 hover:scale-105 transition-transform disabled:opacity-50"
            data-testid="credits-save-btn">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Enregistrer
          </button>
        )}
      </div>

      <div>
        <p className="text-zinc-400 text-xs uppercase tracking-wider mb-2">IA Musculation / Cardio & Coach certifié</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {field('Crédits IA inclus', 'ai_included_remaining', 'Génération musculation OU cardio (pool commun)')}
          {field('Crédits IA achetés', 'ai_purchased')}
          {field('Demandes coach', 'coach_request_remaining', 'Programme fait par un coach certifié')}
        </div>
      </div>

      <div>
        <p className="text-zinc-400 text-xs uppercase tracking-wider mb-2">Plan alimentaire</p>
        <div className="grid grid-cols-2 gap-2">
          {field('Crédits inclus (mois en cours)', 'included_remaining')}
          {field('Crédits achetés', 'purchased', "S'accumulent, n'expirent pas")}
        </div>
      </div>

      <div>
        <p className="text-zinc-400 text-xs uppercase tracking-wider mb-2">Bibliothèque d'exercices personnalisée ("Ma bibliothèque créée")</p>
        <div className="grid grid-cols-2 gap-2">
          {field('Crédits inclus (mois en cours)', 'custom_ex_included_remaining', 'Texte gratuit, image -2, storyboard -1, vidéo Sora 2 -5')}
          {field('Crédits achetés', 'custom_ex_purchased_remaining', "Pool SÉPARÉ des crédits IA muscu/cardio ci-dessus, n'expirent pas")}
        </div>
      </div>
    </div>
  );
};

export const VipNotesEditor = ({ vipDraft, setVipDraft, onSave, dirty, saving, saveError }) => {
  const [newTag, setNewTag] = useState('');
  const isVip = (vipDraft.tags || []).includes('VIP')
    || (vipDraft.vip_discount_percent > 0)
    || (vipDraft.vip_discount_amount_chf > 0)
    || vipDraft.waive_stripe_fees;

  const addTag = (tag) => {
    const t = (tag || '').trim();
    if (!t) return;
    const tags = vipDraft.tags || [];
    if (tags.includes(t)) return;
    setVipDraft({ ...vipDraft, tags: [...tags, t] });
    setNewTag('');
  };
  const removeTag = (tag) => {
    setVipDraft({ ...vipDraft, tags: (vipDraft.tags || []).filter((t) => t !== tag) });
  };

  return (
    <div className={`border rounded-2xl p-4 sm:p-5 space-y-4 ${isVip ? 'bg-gradient-to-br from-amber-500/10 to-yellow-500/5 border-amber-500/30' : 'bg-zinc-900/40 border-white/10'}`} data-testid="vip-notes-editor">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h4 className="text-white font-semibold inline-flex items-center gap-2">
          {isVip ? <Crown className="w-4 h-4 text-amber-400" /> : <Tag className="w-4 h-4 text-violet-400" />}
          Profil client {isVip && <span className="px-2 py-0.5 bg-amber-500/20 text-amber-300 text-[10px] rounded-full font-bold">VIP</span>}
        </h4>
        {dirty && (
          <button onClick={onSave} disabled={saving}
            className="px-3 py-1.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white text-xs font-semibold rounded-lg inline-flex items-center gap-1.5 hover:scale-105 transition-transform disabled:opacity-50"
            data-testid="vip-save-btn">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Enregistrer
          </button>
        )}
      </div>

      {/* Tags */}
      <div>
        <label className="block text-zinc-400 text-xs uppercase tracking-wider mb-2 inline-flex items-center gap-1"><Tag className="w-3 h-3" /> Tags</label>
        <div className="flex flex-wrap gap-1.5 mb-2">
          {(vipDraft.tags || []).map((t) => (
            <span key={t} className={`px-2 py-1 rounded-full text-xs font-semibold inline-flex items-center gap-1 ${t === 'VIP' ? 'bg-amber-500/20 text-amber-300' : 'bg-violet-500/20 text-violet-300'}`} data-testid={`tag-${t}`}>
              {t}
              <button onClick={() => removeTag(t)} className="hover:bg-white/10 rounded-full p-0.5"><X className="w-2.5 h-2.5" /></button>
            </span>
          ))}
        </div>
        <div className="flex gap-1.5 flex-wrap mb-2">
          {SUGGESTED_TAGS.filter((s) => !(vipDraft.tags || []).includes(s)).slice(0, 5).map((s) => (
            <button key={s} onClick={() => addTag(s)}
              className="px-2 py-1 bg-white/5 hover:bg-white/10 border border-white/10 text-zinc-400 text-[10px] rounded-full inline-flex items-center gap-1">
              <Plus className="w-2.5 h-2.5" /> {s}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <input type="text" value={newTag} onChange={(e) => setNewTag(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag(newTag))}
            placeholder="Ajouter un tag personnalisé…"
            className="flex-1 px-3 py-1.5 bg-zinc-800 border border-white/10 rounded-lg text-white text-xs focus:border-primary focus:outline-none"
            data-testid="tag-new-input" />
          <button onClick={() => addTag(newTag)}
            className="px-3 py-1.5 bg-primary text-white text-xs rounded-lg hover:scale-105 transition-transform" data-testid="tag-add-btn">
            <Plus className="w-3 h-3" />
          </button>
        </div>
      </div>

      {/* Rabais VIP */}
      <div>
        <label className="block text-zinc-400 text-xs uppercase tracking-wider mb-2 inline-flex items-center gap-1"><Crown className="w-3 h-3" /> Rabais VIP (appliqué automatiquement aux paiements Stripe)</label>
        <div className="grid grid-cols-2 gap-2 mb-2">
          <div>
            <label className="block text-zinc-500 text-[10px] mb-1">Rabais en %</label>
            <div className="relative">
              <input type="number" min="0" max="100" step="0.5"
                value={vipDraft.vip_discount_percent || 0}
                onChange={(e) => setVipDraft({ ...vipDraft, vip_discount_percent: parseFloat(e.target.value) || 0 })}
                className="w-full pl-3 pr-7 py-2 bg-zinc-800 border border-white/10 rounded-lg text-white text-sm focus:border-amber-500 focus:outline-none"
                data-testid="vip-discount-percent" />
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 text-xs">%</span>
            </div>
          </div>
          <div>
            <label className="block text-zinc-500 text-[10px] mb-1">Rabais fixe (cumulable)</label>
            <div className="relative">
              <input type="number" min="0" step="10"
                value={vipDraft.vip_discount_amount_chf || 0}
                onChange={(e) => setVipDraft({ ...vipDraft, vip_discount_amount_chf: parseFloat(e.target.value) || 0 })}
                className="w-full pl-3 pr-10 py-2 bg-zinc-800 border border-white/10 rounded-lg text-white text-sm focus:border-amber-500 focus:outline-none"
                data-testid="vip-discount-amount" />
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 text-xs">CHF</span>
            </div>
          </div>
        </div>
        <label className="flex items-center gap-2 cursor-pointer bg-zinc-800/50 px-3 py-2 rounded-lg">
          <input type="checkbox"
            checked={!!vipDraft.waive_stripe_fees}
            onChange={(e) => setVipDraft({ ...vipDraft, waive_stripe_fees: e.target.checked })}
            className="rounded accent-amber-500"
            data-testid="vip-waive-fees" />
          <span className="text-white text-xs">Supprimer les frais Stripe en 2x/3x (+3% / +5%)</span>
        </label>
      </div>

      {/* Notes privées */}
      <div>
        <label className="block text-zinc-400 text-xs uppercase tracking-wider mb-2 inline-flex items-center gap-1"><StickyNote className="w-3 h-3" /> Notes privées (visibles admin uniquement)</label>
        <textarea
          rows={3}
          maxLength={2000}
          value={vipDraft.notes || ''}
          onChange={(e) => setVipDraft({ ...vipDraft, notes: e.target.value })}
          placeholder="Préférences, allergies, infos importantes pour le coaching…"
          className="w-full px-3 py-2 bg-zinc-800 border border-white/10 rounded-lg text-white text-sm placeholder-zinc-500 focus:border-amber-500 focus:outline-none resize-none"
          data-testid="vip-notes" />
        <div className="text-right text-zinc-500 text-[10px] mt-1">{(vipDraft.notes || '').length} / 2000</div>
      </div>

      {!dirty && vipDraft.updated_at && (
        <div className="text-zinc-500 text-[10px] text-right">
          Dernière modification : {new Date(vipDraft.updated_at).toLocaleDateString('fr-CH')} par {vipDraft.updated_by || 'admin'}
        </div>
      )}
    </div>
  );
};
